import React, { useState, useRef, useEffect } from 'react';
import { Bell, Video, BookOpen, FileText, Layers, CheckCheck, X, RefreshCw, UserPlus } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { getCurrentUser } from '../../utils/auth';
import './NotificationBell.scss';

const ICON_MAP = {
  video: Video,
  class_started: Video,
  class_ended: Video,
  assignment_posted: FileText,
  batch_update: Layers,
  new_enquiry: UserPlus,
  'user-plus': UserPlus,
  general: Bell,
  default: Bell,
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
}

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useSocket();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const user = getCurrentUser();
  const isStudent = user?.usertype === 'student';

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleNotifClick = (notif) => {
    if (!notif.read) {
      markAsRead(notif._id);
    }
    // For class_started, students use the Join Class button instead
    if (notif.link && !(isStudent && notif.type === 'class_started')) {
      window.location.href = notif.link;
    }
    setOpen(false);
  };

  const handleJoinClass = (e, notif) => {
    e.stopPropagation();
    if (!notif.read) markAsRead(notif._id);
    const batchId = notif.batchId;
    if (batchId) {
      window.location.href = `/s/classroom/${batchId}`;
    }
    setOpen(false);
  };

  const handleReload = (e) => {
    e.stopPropagation();
    window.location.reload();
  };

  return (
    <div className="notif-bell-wrapper" ref={panelRef}>
      <button
        className="nav-action-btn notif-bell-btn"
        onClick={() => setOpen(prev => !prev)}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          {/* Header */}
          <div className="notif-panel__header">
            <h6 className="notif-panel__title">Notifications</h6>
            <div className="notif-panel__actions">
              {unreadCount > 0 && (
                <button
                  className="notif-panel__mark-all"
                  onClick={markAllAsRead}
                  title="Mark all as read"
                >
                  <CheckCheck size={15} />
                  Mark all read
                </button>
              )}
              <button className="notif-panel__close" onClick={() => setOpen(false)}>
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="notif-panel__list">
            {notifications.length === 0 ? (
              <div className="notif-panel__empty">
                <Bell size={32} strokeWidth={1.5} />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const IconComp = ICON_MAP[notif.type] || ICON_MAP[notif.icon] || ICON_MAP.default;
                return (
                  <div
                    key={notif._id}
                    className={`notif-item ${!notif.read ? 'notif-item--unread' : ''}`}
                    onClick={() => handleNotifClick(notif)}
                  >
                    <div className={`notif-item__icon notif-item__icon--${notif.type || 'general'}`}>
                      <IconComp size={16} />
                    </div>
                    <div className="notif-item__content">
                      <p className="notif-item__title">{notif.title}</p>
                      <p className="notif-item__message">{notif.message}</p>
                      {/* Action buttons for class_started notifications (students) */}
                      {isStudent && notif.type === 'class_started' && notif.batchId && (
                        <div className="notif-item__actions">
                          <button
                            className="notif-item__join-btn"
                            onClick={(e) => handleJoinClass(e, notif)}
                          >
                            <Video size={13} />
                            Join Class
                          </button>
                          <button
                            className="notif-item__reload-btn"
                            onClick={handleReload}
                            title="Reload page"
                          >
                            <RefreshCw size={13} />
                          </button>
                        </div>
                      )}
                      <span className="notif-item__time">{timeAgo(notif.createdAt)}</span>
                    </div>
                    {!notif.read && <span className="notif-item__dot" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
